// taken from documents store but modified

import api from "@/api";
import { getField, updateField } from "vuex-map-fields";
import dayjs from "dayjs";

const endpoint = "/eformmanagement/eform";

function hasProp(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

function initialState() {
  return {
    apiEndpoint: endpoint,
    loading: false,
    docType: null,
    forms: [],
    currentForm: {
      status: "draft",
      current_version: {
        version: "1.0",
      },
      new_version: "1.0",
      private: false,
      user_ids: [],
      anonymous: false,
      multiple: false,
    },
    formVersions: {},
    formSubmissions: [],
    loadingFormVersions: false,
    user_filter: false,
    filters: {
      categories: [],
      searchQuery: "",
      status: "approved",
    },
    sortBy: {
      field: "created_time",
      direction: "desc",
    },
    tabFilter: "approved",
    favourites: [],
    currentVersion: null,
    pagination: {
      total: 10,
      per_page: 10,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 10,
    },
  };
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    formattedVersions: (state) => {
      if (!state.formVersions[state.currentForm.id]) {
        return [];
      }
      return state.formVersions[state.currentForm.id]
        .map((version) => ({
          id: version.id,
          version: version.version,
        }))
        .reverse();
    },
    formEntryTitles: (state, getters) => {
      const results = getters.formattedSubmissions;
      const questions = [];
      const ids = [];

      // have to go through each answer
      results.forEach((res) => {
        res.answers.forEach((ans) => {
          const obj = {
            id: ans.id,
            value: ans.question,
          };
          if (!ids.includes(ans.origId)) {
            questions.push(obj);
            ids.push(ans.origId);
          }
        });
      });

      return questions.sort((a, b) => a.id - b.id);
    },
    formattedSubmissions: (state) => {
      const responses = state.formSubmissions;
      const toReturn = responses.map((resp) => {
        const answers = JSON.parse(resp.data);

        const returnObj = {
          dateSubmitted: dayjs(resp.created_time).format("DD/MM/YYYY HH:mm"),
          user: {
            id: resp.user_id,
            name: resp.user_id,
          },
          answers: [],
        };
        const answerArray = [];
        Object.keys(answers).forEach((answerTitle) => {
          const titleBits = answerTitle.split("_");

          // try to format the answers
          const originalAnswer = answers[answerTitle];
          let formattedAnswer = originalAnswer;

          // are we a date?
          const dateRegex = /\d{4}-\d{1,2}-\d{1,2}/gm;

          if (dateRegex.test(originalAnswer)) {
            formattedAnswer = dayjs(originalAnswer).format("DD/MM/YYYY");
          }

          // are we an object or array?
          if (["object", "array"].includes(typeof originalAnswer)) {
            if (originalAnswer[0].value) {
              formattedAnswer = originalAnswer
                .map((item) => item.value)
                .join(", ");
            } else {
              formattedAnswer = originalAnswer.join(", ");
            }
          }

          // if (originalAnswer)

          const getElement = state.currentVersion.elements.filter(
            (elem) => elem.id == titleBits[1]
          );
          answerArray.push({
            id: titleBits[1],
            question: titleBits[0],
            answer: formattedAnswer,
          })
        })

        returnObj.answers = answerArray.sort((a, b) => a.id - b.id)

        return returnObj
      })
      return toReturn
    },
    form: (state) => state.currentForm,

    dataLoaded(state) {
      return !!state.forms
    },

    versions: (state) => {
      const versions = state.formVersions[0]
      const filtered = versions.filter((vers) => {
        if (vers.version !== state.form.current_version_id) {
          return vers
        }
        return null
      })
      return filtered.sort((a, b) => b.id - a.id)
    },

    getField,
  },

  mutations: {
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total;
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page);
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page;
    },
    SET_CURRENT_VERSION(state, payload) {
      state.currentVersion = payload;
    },
    SET_FORM_ENTRIES(state, payload) {
      state.formSubmissions = payload;
    },
    setForms(state, documents) {
      state.forms = documents;
    },

    setCurrentForm(state, form) {
      state.currentForm = { ...form };
    },

    setSortBy(state, { direction, field }) {
      // set direction to 'asc' if not set
      const newDirection = direction || "asc";

      state.sortBy.direction = newDirection;
      state.sortBy.field = field;
    },

    setSearchQuery(state, searchQuery) {
      state.filters.searchQuery = searchQuery;
    },

    setFilterCategories(state, categories) {
      state.filters.categories = categories;
    },

    reset(state) {
      // acquire initial state
      const s = initialState();
      Object.keys(s).forEach((key) => {
        if (
          typeof s[key] === "object" &&
          s[key] === Object(s[key]) &&
          s[key] !== null
        ) {
          state[key] = Object.assign({}, s[key]);
        } else {
          state[key] = s[key];
        }
      });
    },

    clearFilters(state) {
      state.filters.categories = [];
      state.filters.searchQuery = "";
    },

    setFavourites(state, favourites) {
      state.favourites = favourites;
    },

    setTabFilter(state, tabFilter) {
      state.tabFilter = tabFilter;
    },

    addFormVersions(state, versionData) {
      state.formVersions[versionData.formId] = versionData.versions;
    },

    SET_USER_FILTER(state, val) {
      state.user_filter = val;
    },

    setPage(state, page) {
      state.pagination.current_page = page;
    },

    updateField,
  },

  actions: {
    reset({ commit }) {
      commit("reset");
    },

    /**
     * Submit a form
     */

    async submitResponse({ state }, payload) {
      let response;
      const { id } = state.currentForm;

      try {
        response = await api().post(`/eformmanagement/eform/${id}/entry`, {
          entry: { data: JSON.stringify(payload) },
        });
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(new Error(err));
      }
    },

    /**
     * Get submissions
     */
    async getEntries({ commit, rootState }, params) {
      let response;
      let filter = {};

      if (params && params.currentUser) {
        filter = {
          filter: {
            query: {
              bool: {
                must: {
                  term: {
                    user_id: rootState.auth.currentUser.id,
                  },
                },
              },
            },
          },
        };
      }

      try {
        if (params.currentUser) {
          response = await api().put(
            `/eformmanagement/eform/${params.formId}/entries/flat`,
            filter
          );
        } else {
          response = await api().get(
            `/eformmanagement/eform/${params.formId}/entries`
          );
        }
        commit("SET_FORM_ENTRIES", response.data.data.entry);
        return Promise.resolve(response.data.data.entry);
      } catch (err) {
        commit("SET_FORM_ENTRIES", []);
        return Promise.resolve([]);
      }
    },

    /**
     * Generate a CSV file for the current form
     */
    generateCsv({ getters, state }, entries) {
      if (entries) {
        const csvData = [];

        const results = entries;
        const questions = [];
        const ids = [];

        // have to go through each answer
        results.forEach((res) => {
          res.answers.forEach((ans) => {
            const obj = {
              id: ans.id,
              value: ans.question,
            };
            if (!ids.includes(ans.origId)) {
              questions.push(obj);
              ids.push(ans.origId);
            }
          });
        });

        const titles = questions.sort((a, b) => a.id - b.id);

        const submissions = entries;
        const formattedTitles = titles.map((title) => title.value);

        // titles
        if (!state.currentForm.anonymous) {
          csvData.push(["Date Submitted", "Submitted By", ...formattedTitles]);
        } else {
          csvData.push(["Date Submitted", ...formattedTitles]);
        }

        // responses
        let formattedAnswers = [];
        submissions.forEach((entry) => {
          formattedAnswers = entry.answers.map((ans) => {
            if (!ans.isFile || ans.answer == "N/A") {
              return `"${ans.answer}"`;
            }
            const images = `${ans.answer}`.split(",");
            let returnArr = [];
            images.forEach((img) => {
              returnArr.push(
                `${window.location.protocol}//${window.location.hostname}/admin/file/${img}`
              );
            });
            return `"${returnArr.join(", ")}"`;
          });
          if (!state.currentForm.anonymous) {
            csvData.push([
              `${entry.dateSubmitted}`,
              `${entry.user ? entry.user.name : "N/A"} (${
                entry.user ? entry.user.email : "N/A"
              })`,
              ...formattedAnswers,
            ]);
          } else {
            csvData.push([`${entry.dateSubmitted}`, ...formattedAnswers]);
          }
        });

        const csvContent = csvData.map((e) => e.join(",")).join("\n");

        return Promise.resolve(csvContent);
      }

      return false;
    },

    // FORM CRUD ACTIONS

    /**
     * Pending and rejected need to go through versions, not documents
     */
    list({ commit, state }, options) {
      commit("setForms", []);
      state.loading = true;
      const page = state.pagination.current_page;
      let flat = options.flat || false;

      const { tabFilter } = state;

      let listFavourites = false;

      const size = state.pagination.per_page;
      let from = size * page - size;

      if (!from) {
        from = 0;
      }

      const statusFilter =
        tabFilter && tabFilter !== "starred" ? tabFilter : "approved";

      const filter = {
        sort: [
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        size,
        from,
        query: {
          bool: {
            must: [
              {
                bool: {
                  should: [
                    {
                      bool: {
                        should: [
                          {
                            match: {
                              eform_title: {
                                query: state.filters.searchQuery,
                                operator: "and",
                                zero_terms_query: "all",
                                fuzziness: "AUTO",
                              },
                            },
                          },
                          {
                            match_phrase_prefix: {
                              eform_title: {
                                query: state.filters.searchQuery,
                                slop: 50,
                              },
                            },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      };

      if (statusFilter) {
        // review and lapsed need to be date based
        switch (statusFilter) {
          case "review":
            filter.query.bool.must.push([
              {
                range: {
                  review_date: {
                    gte: dayjs().unix(),
                    lte: dayjs()
                      .add(14, "day")
                      .unix(),
                  },
                },
              },
              {
                term: {
                  status: "approved",
                },
              },
            ]);
            break;
          case "lapsed":
            filter.query.bool.must.push([
              {
                range: {
                  review_date: {
                    lte: dayjs().unix(),
                  },
                },
              },
              {
                term: {
                  status: "approved",
                },
              },
            ]);
            break;
          // everything else is status
          default:
            filter.query.bool.must.push({
              term: {
                status: statusFilter,
              },
            });
            break;
        }
      }

      if (state.filters.categories.length >= 1) {
        filter.query.bool.must.push({
          terms: {
            category: state.filters.categories,
          },
        });
      }

      if (tabFilter === "starred") {
        listFavourites = true;
      }

      /**
       * If there is a tab filter, and we are admin, get everything
       * Else retrict to what the user id is.
       * IF we are restricting by the user, then obviously it depends on the tab
       */
      if (state.user_filter) {
        const submittedByParam = ["pending", "rejected"];

        if (submittedByParam.includes(statusFilter)) {
          filter.query.bool.must.push({
            term: {
              created_user_id: state.user_filter,
            },
          });
        } else {
          filter.query.bool.must.push({
            bool: {
              should: [
                {
                  term: {
                    primary_user_id: state.user_filter,
                  },
                },
                {
                  term: {
                    secondary_user_id: state.user_filter,
                  },
                },
              ],
            },
          });
        }
      }

      const versionChecks = ["pending", "rejected", "lapsed", "review"];

      if (versionChecks.includes(statusFilter)) {
        return new Promise((resolve, reject) => {
          api()
            .put("/eformmanagement/versions", {
              filter,
            })
            .then((forms) => {
              const returnDocs = forms.data.data.version.map((doc) => ({
                ...doc,
                current_version: doc,
              }));
              commit("SET_FILTER_TOTAL", forms.data.meta.filter_total);
              commit("SET_CURRENT_PAGE", page);
              commit("setForms", returnDocs);
              resolve(returnDocs);
            })
            .catch((error) => {
              commit("SET_FILTER_TOTAL", 0);
              commit("SET_CURRENT_PAGE", page);
              commit("setForms", []);
              resolve([]);
            })
            .finally(() => {
              setTimeout(() => {
                state.loading = false;
              }, 100);
            });
        });
      }
      let endpoint = `${state.apiEndpoint}s`;
      if (flat) {
        endpoint = `${endpoint}/flat`;
      }
      return new Promise((resolve, reject) => {
        api()
          .put(endpoint, { filter })
          .then((resp) => {
            commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
            commit("SET_CURRENT_PAGE", page);
            commit("setForms", resp.data.data.eform);
            resolve(resp.data.data.eform);
          })
          .catch((error) => {
            commit("SET_FILTER_TOTAL", 0);
            commit("SET_CURRENT_PAGE", page);
            commit("setForms", []);
            resolve([]);
          })
          .finally(() => {
            setTimeout(() => {
              state.loading = false;
            }, 100);
          });
      });
    },

    // get content, forms and policies, limit by 10 though and filter by name/title
    async listAllForSelect(context, filterTerm) {
      const returnArr = {};
      const limit = 5;

      // content first
      const contentQuery = {
        query: {
          bool: {
            must: [
              {
                terms: {
                  status: ["approved", "pending"],
                },
              },
            ],
          },
        },
      };

      if (filterTerm) {
        contentQuery.query.bool.must.push({
          match_phrase_prefix: {
            title: {
              query: filterTerm,
              slop: 10,
            },
          },
        });
      }

      contentQuery.size = limit;
      contentQuery.from = 0;
      returnArr.content = null;
      try {
        const contentResults = await api().put("/eformmanagement/forms/flat", {
          filter: contentQuery,
        });
        returnArr.content = contentResults.data.data.page;
      } catch (err) {
        delete returnArr.content;
      }

      // TODO: Currently we only want pages, so this needs to remove later
      return Promise.resolve(returnArr);
    },

    create({ state, dispatch }, documentData) {
      return new Promise((resolve, reject) => {
        api()
          .put(`${state.apiEndpoint}`, { eform: documentData })
          .then((form) => {
            dispatch(
              "notifications/create",
              {
                body: "The form was created",
                type: "success",
              },
              { root: true }
            );

            resolve(form);
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: "The form failed to be created",
                type: "danger",
              },
              { root: true }
            );

            reject(error);
          });
      });
    },

    async read({ commit, state, dispatch }, { formId, versionId }) {
      commit("setCurrentForm", {});
      let form;

      try {
        form = await api().get(`${state.apiEndpoint}/${formId}`);
        if (versionId) {
          await dispatch("getVersion", { formId, versionId });
        }
        commit("setCurrentForm", form.data.data.eform[0]);

        if (form.data.data.eform[0].current_version && !versionId) {
          commit(
            "SET_CURRENT_VERSION",
            form.data.data.eform[0].current_version
          );
        }

        return Promise.resolve(form.data.data.eform[0]);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async retrieveByUrl({ commit }, url) {
      commit("setCurrentForm", {});

      let response;

      const filter = {
        filter: {
          query: {
            bool: {
              must: [
                {
                  term: {
                    eform_url: url,
                  },
                },
              ],
            },
          },
        },
      };

      try {
        response = await api().put("/eformmanagement/eforms", filter);
        commit("setCurrentForm", response.data.data.eform[0]);
        return Promise.resolve(response.data.data.eform[0]);
      } catch (err) {
        commit("setCurrentForm", {});
        return Promise.reject(new Error(err));
      }
    },

    delete({ state, dispatch }, formId) {
      return new Promise((resolve, reject) => {
        api()
          .delete(`${state.apiEndpoint}/${formId}`)
          .then((form) => {
            dispatch(
              "notifications/create",
              {
                body: "The form was deleted",
                type: "success",
              },
              { root: true }
            );

            dispatch("list", { flat: true });
            resolve();
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: "The form could not be deleted",
                type: "danger",
              },
              { root: true }
            );
            reject(error);
          });
      });
    },

    listVersions({ commit, state }, formId) {
      state.loadingDocVersions = true;
      return new Promise((resolve, reject) => {
        api()
          .get(`${state.apiEndpoint}/${formId}/versions/flat`)
          .then((versions) => {
            commit("addFormVersions", {
              formId,
              versions: versions.data.data.version,
            });
            resolve(versions);
            state.loadingDocVersions = false;
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    createVersion({ state, dispatch }, { formId, version, saveAsDraft }) {
      const { apiEndpoint } = state;

      const requestEndpoint = `${apiEndpoint}/${formId}/version`;
      const requestBody = {
        version,
        past_date_bypass: true,
        draft: saveAsDraft,
      };

      return new Promise((resolve, reject) => {
        api()
          .put(requestEndpoint, requestBody)
          .then((response) => {
            if (response.data.data) {
              const formVersion = response.data.data.version[0];
              resolve(formVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Form Version Created",
                  body: "The form version was created",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(response.data);
            }
          })
          .catch((error) => {
            if (
              error.response &&
              hasProp(error.response.data.data, "version")
            ) {
              const formVersion = error.response.data.data.version[0];
              resolve(formVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Form Version Created",
                  body: "The form version was created",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(error.response.data);
            }
          });
      });
    },

    updateVersion(
      { state, dispatch },
      { formId, versionId, version, saveAsDraft }
    ) {
      const { apiEndpoint } = state;

      const requestEndpoint = `${apiEndpoint}/${formId}/version/${versionId}`;
      const requestBody = {
        version,
        past_date_bypass: true,
        draft: saveAsDraft,
      };

      return new Promise((resolve, reject) => {
        api()
          .put(requestEndpoint, requestBody)
          .then((response) => {
            if (response.data.data) {
              const formVersion = response.data.data.version[0];
              resolve(formVersion);
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Form Version Updated",
                  body: "The form version was updated",
                  type: "success",
                },
                { root: true }
              );
            } else {
              reject(response.data);
            }
          })
          .catch((error) => {
            if (
              error.response &&
              hasProp(error.response.data.data, "version")
            ) {
              const formVersion = error.response.data.data.version[0];
              // Send notification
              dispatch(
                "notifications/create",
                {
                  title: "Form Version Updated",
                  body: "The form version was updated",
                  type: "success",
                },
                { root: true }
              );
              resolve(formVersion);
            } else {
              reject(error);
            }
          });
      });
    },

    approveForm({ state, dispatch }, { formId, versionId }) {
      const url = `${endpoint}/${formId}/version/${versionId}/approve`;
      return new Promise((resolve, reject) => {
        api()
          .put(url)
          .then((version) => {
            dispatch(
              "notifications/create",
              {
                body: "The form was approved",
                type: "success",
              },
              { root: true }
            );
            resolve(version);
          })
          .catch((error) => {
            dispatch(
              "notifications/create",
              {
                title: "Error!",
                body: "The form could not be approved",
                type: "danger",
              },
              { root: true }
            );
            reject(error);
          });
      });
    },

    // rejectDocument({ state, dispatch }, { documentId, versionId }) {
    //   return new Promise((resolve, reject) => {
    //     documentVersionsApi
    //       .reject(state.docType, documentId, versionId)
    //       .then((version) => {
    //         dispatch(
    //           'notifications/create',
    //           {
    //             body: `The ${state.docType.singular} was rejected`,
    //             type: 'success',
    //           },
    //           { root: true },
    //         );
    //         resolve(version);
    //       })
    //       .catch((error) => {
    //         dispatch(
    //           'notifications/create',
    //           {
    //             title: 'Error!',
    //             body: `The ${state.docType.singular} could not be rejected`,
    //             type: 'danger',
    //           },
    //           { root: true },
    //         );
    //         reject(error);
    //       });
    //   });
    // },

    async getVersion({ commit }, { formId, versionId }) {
      let resp;

      try {
        resp = await api().get(
          `/eformmanagement/eform/${formId}/version/${versionId}`
        );
        commit("SET_CURRENT_VERSION", resp.data.data.version[0]);
        return Promise.resolve(resp.data.data.version[0]);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // favourite({ state, dispatch }, documentId) {
    //   documentsApi.favourite(state.docType, documentId).then((document) => {
    //     dispatch(
    //       'notifications/create',
    //       {
    //         body: `The ${state.docType.singular} <b>${document.current_version.title}</b> has been favorited`,
    //         type: 'success',
    //       },
    //       { root: true },
    //     );
    //   });
    // },

    // unfavourite({ state }, documentId) {
    //   documentsApi.unfavourite(state.docType, documentId);
    // },

    createAndPublish({ dispatch, rootGetters }, payload) {
      const documentData = payload.payload;
      const { saveAsDraft } = payload;
      const version = {
        title: documentData.current_version.title,
        version: documentData.current_version.version,
        elements: rootGetters["pageElements/forServer"] || null,
      };

      return new Promise((resolve, reject) => {
        dispatch("create", documentData)
          .then((document) => {
            dispatch("createVersion", {
              formId: document.data.data.eform[0].id,
              version,
              saveAsDraft,
            })
              .then((documentVersion) => {
                resolve({
                  document,
                  version: documentVersion,
                });
              })
              .catch((error) => reject(error));
          })
          .catch((error) => reject(error));
      });
    },

    /**
     * This is for just updating details about the document, not version
     */
    updateDocument({ state }, documentData) {
      const payload = {};
      payload[state.docType.singular] = documentData;

      return new Promise((resolve, reject) => {
        api()
          .post(`${state.apiEndpoint}/${documentData.id}/`, payload)
          .then((document) => {
            resolve(document);
          })
          .catch((error) => {
            console.log("error", { ...error });
            reject(error);
          });
      });
    },

    update({ state, dispatch, rootGetters }, document) {
      const documentData = document;
      const version = {
        title: documentData.pageData.current_version.title,
        version:
          documentData.version || documentData.pageData.current_version.version,
        elements: rootGetters["pageElements/forServer"] || null,
      };

      let vAction = "updateVersion";
      let vPayload = {
        formId: documentData.formId,
        versionId: documentData.pageData.current_version.id,
        version,
      };

      /**
       * Tags. just need ids
       */
      if (documentData.pageData.tags) {
        documentData.pageData.tag_ids = documentData.pageData.tags.map(
          (tag) => tag.id
        );
      }

      /**
       * If we just updating, we need to go to updateVersion
       * If we have a new file and new version number, we need to go to createVersion
       */

      if (
        (documentData.pageData.current_version.orig_version &&
          documentData.pageData.current_version.orig_version !==
            version.version.toString()) ||
        !documentData.pageData.current_version.orig_version
      ) {
        vAction = "createVersion";
        vPayload = { formId: documentData.formId, version };
      }

      const payload = documentData.pageData;

      return new Promise((resolve, reject) => {
        api()
          .post(`${state.apiEndpoint}/${documentData.formId}`, {
            eform: payload,
          })
          .then((document) => {
            dispatch(vAction, vPayload)
              .then((documentVersion) => {
                resolve({
                  document,
                  version: documentVersion,
                });
              })
              .catch((error) => reject(error));
          })
          .catch((error) => {
            console.log("error", { ...error });
            reject(error);
          });
      });
    },

    resubmit({ dispatch }, { formId, pageData, version }) {
      return new Promise((resolve, reject) => {
        dispatch("update", { formId, pageData, version })
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            dispatch(
              "notifications/create",
              {
                body: "There was a problem saving the page.",
                type: "danger",
              },
              { root: true }
            );
            reject(err);
          });
      });
    },

    async getUserDocCount({ state, rootState, dispatch }) {
      let userId;

      if (!rootState.auth.currentUser.id) {
        await dispatch("auth/listCurrentUser", {}, { root: true }).then(
          (resp) => {
            userId = resp.id;
          }
        );
      } else {
        userId = rootState.auth.currentUser.id;
      }

      const payload = {
        filter: {
          query: {
            bool: {
              should: [
                {
                  term: {
                    primary_user_id: userId,
                  },
                },
                {
                  term: {
                    created_user_id: userId,
                  },
                },
                {
                  term: {
                    secondary_user_id: userId,
                  },
                },
              ],
            },
          },
        },
        size: 1,
        from: 0,
      };

      let response;

      try {
        response = await api().put(
          `/${state.docType.singular}management/${state.docType.plural}`,
          payload
        );
        return Promise.resolve(response.data.meta.filter_total);
      } catch (err) {
        return Promise.resolve(0);
      }
    },
  },
};
